import { format } from 'date-fns'
import { formatDateBR } from './dateUtils'

interface ReportRow {
  Id: number
  CodigoCliente: string | null
  Cliente: string | null
  Data: string | Date | null
  Valor: number | null
  Natureza: string | null
  Nota: string | null
  Job: string | null
}

export async function exportGerencialToExcel(data: ReportRow[]): Promise<void> {
  // Dynamic import to keep bundle small
  const ExcelJS = await import('exceljs')
  const { saveAs } = await import('file-saver')

  const workbook = new ExcelJS.Workbook()
  const sheet = workbook.addWorksheet('Fluxo Gerencial')

  // Header row
  const headers = ['Data', 'Cliente', 'Job', 'Valor', 'Natureza', 'Nota']
  const headerRow = sheet.addRow(headers)
  headerRow.eachCell((cell) => {
    cell.font = { bold: true }
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFE0E0E0' },
    }
  })

  // Data rows — date formatted in UTC to avoid local-zone day shift.
  data.forEach((row) => {
    const dateValue = row.Data ? formatDateBR(row.Data) : ''

    sheet.addRow([
      dateValue,
      row.Cliente || '',
      row.Job || '',
      row.Valor ?? 0,
      row.Natureza || '',
      row.Nota || '',
    ])
  })

  // Format Valor column as currency
  sheet.getColumn(4).numFmt = 'R$ #,##0.00'
  sheet.getColumn(4).alignment = { horizontal: 'right' }

  // Auto-size columns
  sheet.columns.forEach((column) => {
    let maxLength = 0
    column.eachCell?.({ includeEmpty: true }, (cell) => {
      const cellLength = cell.value ? String(cell.value).length : 10
      if (cellLength > maxLength) maxLength = cellLength
    })
    column.width = Math.min(Math.max(maxLength + 2, 10), 40)
  })

  // Generate and download
  const buffer = await workbook.xlsx.writeBuffer()
  const timestamp = format(new Date(), 'yyyy-MM-dd_HHmm')
  saveAs(new Blob([buffer]), `FluxoGerencial_${timestamp}.xlsx`)
}

/**
 * Download a blank XLSX template with the expected import columns.
 * Users fill this in and upload it through the import dropzone.
 */
export async function downloadBlankTemplate(): Promise<void> {
  const ExcelJS = await import('exceljs')
  const { saveAs } = await import('file-saver')

  const workbook = new ExcelJS.Workbook()
  const sheet = workbook.addWorksheet('Plan1')

  const headers = [
    'DATA', 'FAVORECIDO', 'CONTA', 'MEMO', 'PIT', 'MONTANTE',
    'ENTRADA', 'SAIDA', 'CENTRO DE RESULTADO', 'DESCRIÇÃO',
    'NATUREZA', 'DESCRIÇÃO2', 'AJUSTE DESCRIÇÃO', 'MÊS',
  ]

  const headerRow = sheet.addRow(headers)
  headerRow.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } }
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF2A2D34' },
    }
    cell.border = {
      bottom: { style: 'thin', color: { argb: 'FFD2FA52' } },
    }
  })

  // Column widths matching expected content
  const widths = [14, 25, 20, 12, 14, 14, 14, 14, 22, 30, 10, 30, 25, 12]
  widths.forEach((w, i) => {
    sheet.getColumn(i + 1).width = w
  })

  // Format hints: currency columns (MONTANTE=6, ENTRADA=7, SAIDA=8)
  ;[6, 7, 8].forEach((col) => {
    sheet.getColumn(col).numFmt = '#,##0.00'
  })

  // Date column
  sheet.getColumn(1).numFmt = 'dd/mm/yyyy'

  const buffer = await workbook.xlsx.writeBuffer()
  saveAs(new Blob([buffer]), 'Modelo_FluxoGerencial.xlsx')
}

/**
 * Download a blank CSV template with the expected import columns.
 */
export async function downloadBlankTemplateCsv(): Promise<void> {
  const { saveAs } = await import('file-saver')

  const headers = [
    'DATA', 'FAVORECIDO', 'CONTA', 'MEMO', 'PIT', 'MONTANTE',
    'ENTRADA', 'SAIDA', 'CENTRO DE RESULTADO', 'DESCRIÇÃO',
    'NATUREZA', 'DESCRIÇÃO2', 'AJUSTE DESCRIÇÃO', 'MÊS',
  ]

  const bom = '\uFEFF' // UTF-8 BOM for Excel compatibility
  const csv = bom + headers.join(';') + '\n'
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  saveAs(blob, 'Modelo_FluxoGerencial.csv')
}
